import { Building2, Code2, CreditCard, type LucideIcon } from 'lucide-react';

/**
 * Who may open a topic: everyone in the company, the admins and managers who see what the company
 * spends (`seesBilling`), the admins alone, or whoever reads the lists of people and API keys
 * (`readsPeople`: the admins, since 2026-10-06). The same rules the pages always had: Recent changes
 * and Leaving were the admin's, Billing the admin's and the manager's.
 */
export type TopicAudience = 'member' | 'billing' | 'admin' | 'people';

/** What this person may open, from their role (`tenant/role.ts`). */
export interface TopicReach {
  admin: boolean;
  billing: boolean;
  people: boolean;
}

/** The small count beside a topic's name in the menu, from a read the page already makes. */
export type TopicCount = 'people' | 'keys';

export interface SettingsTopic {
  to: TopicPath;
  label: string;
  audience: TopicAudience;
  count?: TopicCount;
}

export interface SettingsGroup {
  label: string;
  icon: LucideIcon;
  audience: TopicAudience;
  topics: SettingsTopic[];
}

export type TopicPath =
  | '/t/$tenant/settings/company/general'
  | '/t/$tenant/settings/company/people'
  | '/t/$tenant/settings/company/security'
  | '/t/$tenant/settings/company/documents'
  | '/t/$tenant/settings/company/devices'
  | '/t/$tenant/settings/company/activity'
  | '/t/$tenant/settings/billing/plan'
  | '/t/$tenant/settings/billing/alerts'
  | '/t/$tenant/settings/billing/usage'
  | '/t/$tenant/settings/billing/partner'
  | '/t/$tenant/settings/developers/keys'
  | '/t/$tenant/settings/developers/send';

/**
 * Company settings as three groups of short topics, one page and one address each, so support can
 * link straight to "Billing, Credit alerts" (Dustin, 2026-10-02, layout B). A group's first topic is
 * where the group's old address opens.
 */
export const settingsGroups: SettingsGroup[] = [
  {
    label: 'Company',
    icon: Building2,
    audience: 'member',
    topics: [
      { to: '/t/$tenant/settings/company/general', label: 'General', audience: 'member' },
      {
        to: '/t/$tenant/settings/company/people',
        label: 'People',
        audience: 'people',
        count: 'people',
      },
      { to: '/t/$tenant/settings/company/security', label: 'Security', audience: 'member' },
      { to: '/t/$tenant/settings/company/documents', label: 'Documents', audience: 'member' },
      {
        to: '/t/$tenant/settings/company/devices',
        label: 'Scanners and devices',
        audience: 'member',
      },
      { to: '/t/$tenant/settings/company/activity', label: 'Activity', audience: 'admin' },
    ],
  },
  {
    label: 'Billing',
    icon: CreditCard,
    audience: 'billing',
    topics: [
      { to: '/t/$tenant/settings/billing/plan', label: 'Plan and pages', audience: 'billing' },
      { to: '/t/$tenant/settings/billing/alerts', label: 'Page alerts', audience: 'billing' },
      { to: '/t/$tenant/settings/billing/usage', label: 'Usage', audience: 'billing' },
      { to: '/t/$tenant/settings/billing/partner', label: 'Partner', audience: 'billing' },
    ],
  },
  {
    label: 'Developers',
    icon: Code2,
    audience: 'member',
    topics: [
      {
        to: '/t/$tenant/settings/developers/keys',
        label: 'API keys',
        audience: 'people',
        count: 'keys',
      },
      {
        to: '/t/$tenant/settings/developers/send',
        label: 'Send from your systems',
        audience: 'member',
      },
    ],
  },
];

/** What a person may open: admins everything, managers billing too, everyone else the members' topics. */
export function opens(audience: TopicAudience, can: TopicReach): boolean {
  if (audience === 'admin') return can.admin;
  if (audience === 'billing') return can.billing;
  if (audience === 'people') return can.people;
  return true;
}

/** The groups and topics this person sees, in menu order; a group with nothing left goes. */
export function visibleGroups(can: TopicReach): SettingsGroup[] {
  return settingsGroups
    .filter((g) => opens(g.audience, can))
    .map((g) => ({ ...g, topics: g.topics.filter((t) => opens(t.audience, can)) }))
    .filter((g) => g.topics.length > 0);
}

/** The group and topic a pathname is on, for the menu's one-line summary on a phone. */
export function topicAt(
  pathname: string,
  tenantId: string,
): { group: SettingsGroup; topic: SettingsTopic } | null {
  for (const group of settingsGroups)
    for (const topic of group.topics)
      if (pathname === topic.to.replace('$tenant', tenantId)) return { group, topic };
  return null;
}
